import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { SiteTrackingPlugin } from '@snowplow/browser-plugin-site-tracking';
import { type DIDOMI_VENDOR } from '@pkgs/components/consts/consts';
import { initSnowplowTracker, type SelfDescribingJson, trackPageView as snowplowTrackPageView } from '@ev-tech/snowplow-library';

import { mapPropertyTrackingIdsToDataLayerTrackingIds, pushReferrerTrackingToDataLayer } from '@/utils/tracking';
import { enableSnowplowTracking, setSnowplowTracker } from '@/utils/snowplowWindow';
import { updateSnowplowTracking } from '@/utils/cookieUtils';
import { type PropertyTrackingIds } from '@/types/property.types';

// Load Didomi SDK only on the client to avoid SSR window references
const DidomiSDKNoSSR = dynamic(() => import('@didomi/react').then((m) => m.DidomiSDK), { ssr: false });
interface ConsentState {
  hasTrackingConsent: boolean;
  hasYoutubeConsent: boolean;
  hasVimeoConsent: boolean;
}

type DidomiFunctions = {
  showNotice: () => void;
  showPreferences: () => void;
  checkVendorsStatus: (vendor: DIDOMI_VENDOR) => boolean;
  pushGTMEvent: (eventName: DataLayerEventType['event'], eventData?: Record<string, string>) => void;
  pushPropertyTrackingIdsToDataLayer: (propertyTracking: PropertyTrackingIds | undefined) => void;
  pushUrlTrackingIdsToDataLayer: (urlTracking: PropertyTrackingIds | undefined) => void;
  trackPageView: (contexts: SelfDescribingJson[]) => void;
};

type DidomiNotReadyContextValue = {
  isReady: false;
};

type DidomiReadyContextValue = { isReady: true } & DidomiFunctions;

export type DidomiContextValue = {
  isReady: boolean;
  consentState: ConsentState;
  isSnowplowInitialized: boolean;
} & (DidomiNotReadyContextValue | DidomiReadyContextValue);

export const DidomiContext = createContext<DidomiContextValue | null>(null);
const TRACKING_VENDOR_ID = 'engeland-UPm6kfxh';

interface DidomiProviderProps {
  children: ReactNode;
}

export const DidomiProvider = ({ children }: DidomiProviderProps) => {
  const [isSnowplowInitialized, setIsSnowplowInitialized] = useState(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [consentState, setConsentState] = useState<ConsentState>({
    hasTrackingConsent: false,
    hasYoutubeConsent: false,
    hasVimeoConsent: false,
  });
  const [didomiFunctions, setDidomiFunctions] = useState<DidomiFunctions | null>(null);
  // Playwright seeds this flag before navigation; skipping the SDK mount avoids
  // the consent popup entirely and keeps test-only behavior client-side.
  // oxlint-disable-next-line typescript/prefer-optional-chain
  const shouldSkipDidomiSdk = typeof window !== 'undefined' && window.skipConsentCheck === true;

  const checkConsentStatus = useCallback((): ConsentState => {
    if (typeof window === 'undefined' || !window.Didomi) {
      return { hasTrackingConsent: false, hasYoutubeConsent: false, hasVimeoConsent: false };
    }

    const userCookieStatus = window.Didomi.getCurrentUserStatus();
    const vendors = userCookieStatus.vendors;

    const hasTrackingConsent = Boolean(TRACKING_VENDOR_ID in vendors && vendors[TRACKING_VENDOR_ID].enabled);
    const hasYoutubeConsent = Boolean(Object.values(userCookieStatus.vendors).some((item) => item.id.includes('youtube') && item.enabled));
    const hasVimeoConsent = Boolean(Object.values(userCookieStatus.vendors).some((item) => item.id.includes('vimeo') && item.enabled));

    return { hasTrackingConsent, hasYoutubeConsent, hasVimeoConsent };
  }, []);

  const updateConsentState = useCallback(() => {
    const newState = checkConsentStatus();
    setConsentState(newState);

    // Update Snowplow tracking based on consent
    if (process.env.NODE_ENV !== 'test') {
      updateSnowplowTracking(newState.hasTrackingConsent);
    }
  }, [checkConsentStatus]);

  const updateDidomiFunctions = useCallback(() => {
    setDidomiFunctions({
      pushGTMEvent: (eventName: DataLayerEventType['event'], eventData?: Record<string, string>): void => {
        window.dataLayer?.push({
          event: eventName,
          ...eventData,
        });
      },

      pushPropertyTrackingIdsToDataLayer: (trackingIds: PropertyTrackingIds | undefined) => {
        pushReferrerTrackingToDataLayer({
          propertyTrackingIds: mapPropertyTrackingIdsToDataLayerTrackingIds(trackingIds ?? {}),
        });
      },

      pushUrlTrackingIdsToDataLayer: (trackingIds: PropertyTrackingIds | undefined) => {
        pushReferrerTrackingToDataLayer({
          urlTrackingIds: mapPropertyTrackingIdsToDataLayerTrackingIds(trackingIds ?? {}),
        });
      },

      trackPageView: (contexts: SelfDescribingJson[]) => {
        snowplowTrackPageView({
          context: [...contexts],
        });
      },
      showNotice: () => {
        if (typeof window !== 'undefined' && window.Didomi?.notice.show) {
          window.Didomi.notice.show();
        }
      },
      showPreferences: () => {
        if (typeof window !== 'undefined' && window.Didomi?.preferences.show) {
          window.Didomi.preferences.show();
        }
      },
      checkVendorsStatus: (vendor: DIDOMI_VENDOR): boolean => {
        if (typeof window === 'undefined' || !window.Didomi) {
          return false;
        }
        const userCookieStatus = window.Didomi.getCurrentUserStatus();
        const vendors = userCookieStatus.vendors;

        return vendors[vendor]?.enabled ?? false;
      },
    });
  }, [updateConsentState]);

  /**
   * The API is exposed on the window.Didomi object.
   * All calls to the Didomi API (except for the standard IAB __cmp function)
   * must be enclosed within a window.didomiOnReady callback to ensure that the SDK is ready before calling it.
   * https://developers.didomi.io/cmp/web-sdk/reference/api#didomi-ready
   */
  const handleDidomiReady = useCallback(() => {
    setIsReady(true);
    updateConsentState();
    updateDidomiFunctions();
  }, [updateConsentState]);

  useEffect(() => {
    if (!isSnowplowInitialized) {
      // Required by LeadFlows to fire tracking events
      enableSnowplowTracking();

      const tracker = initSnowplowTracker({
        applicationId: 'new-search',
        hasUserAcceptedAgreement: consentState.hasTrackingConsent,
        isProd: window.location.host.includes('engelvoelkers.com'),
        initialTrackPageViewEvent: false,
        plugins: [SiteTrackingPlugin()],
      });
      setSnowplowTracker(tracker);
      setIsSnowplowInitialized(true);
    }
  }, []);

  // Auto-initialize when Didomi is present in window (e.g., tests or runtime)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'test' && window?.Didomi && !isReady) {
      setIsReady(true);
      updateDidomiFunctions();
    }
  }, [isReady]);

  // Listen for external consent change events to keep context in sync
  useEffect(() => {
    const handleExternalConsentChanged = (event: Event) => {
      const customEvent = event as CustomEvent<Partial<ConsentState>>;
      const detail = customEvent.detail;
      if (
        typeof detail.hasTrackingConsent === 'boolean' &&
        typeof detail.hasYoutubeConsent === 'boolean' &&
        typeof detail.hasVimeoConsent === 'boolean'
      ) {
        setConsentState({
          hasTrackingConsent: detail.hasTrackingConsent,
          hasYoutubeConsent: detail.hasYoutubeConsent,
          hasVimeoConsent: detail.hasVimeoConsent,
        });
        if (process.env.NODE_ENV !== 'test') {
          updateSnowplowTracking(detail.hasTrackingConsent);
        }
        return;
      }

      // If no detail provided, recompute from Didomi
      updateConsentState();
    };

    // Note: Didomi React SDK provides callbacks via props (onReady, onConsentChanged).
    // We rely on those in production. The custom 'didomiConsentChanged' window event
    // is only used for test compatibility and legacy helpers.
    if (process.env.NODE_ENV === 'test' && typeof window !== 'undefined') {
      window.addEventListener('didomiConsentChanged', handleExternalConsentChanged);
      return () => {
        window.removeEventListener('didomiConsentChanged', handleExternalConsentChanged);
      };
    }

    return () => undefined;
  }, [updateConsentState]);

  const value: DidomiContextValue = {
    consentState,
    isSnowplowInitialized,
    ...(isReady && didomiFunctions
      ? {
          isReady: true,
          ...didomiFunctions,
        }
      : {
          isReady: false,
        }),
  };

  return (
    <DidomiContext.Provider value={value}>
      {!shouldSkipDidomiSdk && process.env.NEXT_PUBLIC_DIDOMI_API_KEY && (
        <DidomiSDKNoSSR
          noticeId={process.env.NEXT_PUBLIC_DIDOMI_NOTICE_ID}
          apiKey={process.env.NEXT_PUBLIC_DIDOMI_API_KEY}
          iabVersion={2}
          gdprAppliesGlobally={true}
          onReady={handleDidomiReady}
          onConsentChanged={updateConsentState}
        />
      )}
      {children}
    </DidomiContext.Provider>
  );
};

export const useDidomi = (): DidomiContextValue => {
  const context = useContext(DidomiContext);
  if (!context) {
    throw new Error('useDidomi must be used within a DidomiProvider');
  }
  return context;
};
